import { decodeJoinToken, detectSessionUserType, getJoinTokenFromUrl } from './videoJoinToken';

const RETURN_URL_STORAGE_KEY = 'VideoNewApp-returnUrl';
const WAS_IN_CALL_STORAGE_KEY = 'VideoNewApp-wasInCall';

const ALLOWED_ORIGIN_REGEX = /^https?:\/\/((localhost|127\.0\.0\.1)(:\d+)?|([a-z0-9-]+\.)?(estecharat|estsharat|2mdopinion)\.com)(\/|$)/i;

function isAllowedOrigin(origin: string): boolean {
  try {
    const url = new URL(origin);
    return ALLOWED_ORIGIN_REGEX.test(url.origin + '/');
  } catch {
    return false;
  }
}

function portalOriginsFromEnv(): string[] {
  const urls: string[] = [];
  const multi = (process.env.REACT_APP_PORTAL_URLS || '').trim();
  if (multi) {
    for (const part of multi.split(',')) {
      const origin = part.trim().replace(/\/$/, '');
      if (origin && isAllowedOrigin(origin + '/')) {
        urls.push(origin);
      }
    }
  }
  const single = (process.env.REACT_APP_PORTAL_URL || '').trim().replace(/\/$/, '');
  if (single && isAllowedOrigin(single + '/') && !urls.includes(single)) {
    urls.push(single);
  }
  return urls;
}

function getPortalOrigin(): string {
  if (document.referrer) {
    try {
      const ref = new URL(document.referrer);
      if (isAllowedOrigin(ref.origin)) {
        return ref.origin;
      }
    } catch {
      /* ignore */
    }
  }
  const fromEnv = portalOriginsFromEnv();
  if (fromEnv.length > 0) {
    return fromEnv[0];
  }
  const apiBase = (process.env.REACT_APP_API_BASE || '').trim().toLowerCase();
  if (apiBase.includes('api-test.')) {
    return 'https://portal-test.estecharat.com';
  }
  if (apiBase.includes('api.estecharat.com')) {
    return 'https://portal.estecharat.com';
  }
  return '';
}

/** Resolve `return` / `returnUrl` query param to a safe absolute portal URL. */
export function resolveReturnUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (trimmed.startsWith('/')) {
    const portalOrigin = getPortalOrigin();
    if (!portalOrigin) {
      return null;
    }
    return `${portalOrigin}${trimmed}`;
  }
  try {
    const url = new URL(trimmed);
    if (!isAllowedOrigin(url.origin)) {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

export function extractAppointmentIdFromRoom(roomId?: string | null): string | null {
  if (!roomId) {
    return null;
  }
  const match = roomId.trim().match(/^room-(\d+)$/i);
  return match ? match[1] : null;
}

/** Build portal appointment URL when the join link omitted `return`. */
export function buildFallbackReturnUrl(): string | null {
  const token = getJoinTokenFromUrl();
  const roomId = token ? decodeJoinToken(token)?.roomId : null;
  const appointmentId = extractAppointmentIdFromRoom(roomId);
  if (!appointmentId) {
    return null;
  }
  const portalOrigin = getPortalOrigin();
  if (!portalOrigin) {
    return null;
  }
  const userType = detectSessionUserType();
  const dashboardPath = userType === 'doctor' ? '/doctor/dashboard' : '/patient/dashboard';
  return `${portalOrigin}${dashboardPath}?appointmentId=${appointmentId}`;
}

function storeReturnUrl(url: string): string {
  sessionStorage.setItem(RETURN_URL_STORAGE_KEY, url);
  return url;
}

/** Read return URL from query string and persist for post-disconnect navigation. */
export function persistReturnUrlFromQuery(): string | null {
  const params = new URLSearchParams(window.location.search);
  const hasDirectJoinToken = Boolean(params.get('t') || params.get('token'));
  if (hasDirectJoinToken) {
    sessionStorage.removeItem(WAS_IN_CALL_STORAGE_KEY);
  }
  const raw = params.get('return') || params.get('returnUrl');
  if (raw) {
    const resolved = resolveReturnUrl(raw);
    if (resolved) {
      return storeReturnUrl(resolved);
    }
  }
  const stored = getStoredReturnUrl();
  if (stored) {
    return stored;
  }
  const fallback = buildFallbackReturnUrl();
  if (fallback) {
    return storeReturnUrl(fallback);
  }
  return null;
}

export function getStoredReturnUrl(): string | null {
  const stored = sessionStorage.getItem(RETURN_URL_STORAGE_KEY);
  if (!stored) {
    return null;
  }
  return resolveReturnUrl(stored) ?? stored;
}

export function markWasInCall(): void {
  sessionStorage.setItem(WAS_IN_CALL_STORAGE_KEY, '1');
}

export function wasInCall(): boolean {
  return sessionStorage.getItem(WAS_IN_CALL_STORAGE_KEY) === '1';
}

export function shouldShowReturnToAppointment(): boolean {
  return Boolean(getStoredReturnUrl());
}

/** After disconnect, prompt to return to the portal appointment page. */
export function shouldShowReturnAfterCall(): boolean {
  return Boolean(getStoredReturnUrl()) && wasInCall();
}

export function navigateToReturnUrl(): void {
  const url = getStoredReturnUrl();
  if (url) {
    window.location.href = url;
  }
}
